import { anewRListsHttp } from "../../../shared/services/api/anew-r-lists-http";
import { ListNames } from "../../../shared/constants/globalConstants";
import { getAllItemsInStore, getLatestModifiedDate, saveItems } from "../../../store/indexed-db/indexedDBHelper";



const toODataDate = (timestamp: number | string | Date) =>
  new Date(timestamp).toISOString();

type OrderBy = { orderBy: string; ascending: boolean };

type MetadataConfig = {
      store: string;
      listName: string;
      select: string[];
      expand: string[];
      filter: string;
      orderBy: OrderBy[];
      indexBasedOn: string;
    };

type MetadataPriority = "critical" | "deferred";

/**
 * Metadata fetched at app start. "critical" blocks the first usable render;
 * "deferred" is hydrated in the background afterwards. Every entry MUST declare
 * a priority - adding a list without one is a type error, so the split can never
 * drift silently when this array is reordered or extended.
 */
type InitialMetadataConfig = MetadataConfig & { priority: MetadataPriority };


const METADATA_CONFIGS: InitialMetadataConfig[] = [
  {
    store: "TRMCommonMetadataList",
    listName: ListNames.TRMCommonMetadataList,
    select: [
      "eKey", "eTag", "Code", "MetadataType", "MetadataValue",
      "ParentCode", "MetadataDescription", "AdditionalValue3",
      "AdditionalValue1", "ShortName", "ItemCreated", "ItemCreatedBy",
      "ItemModified", "ItemModifiedBy","IsDeleted","IsArchived"
    ],
    expand: ["ItemCreatedBy", "ItemModifiedBy"],
    filter: "",
    orderBy: [],
    indexBasedOn: "ItemModified",
    priority: "critical"
  },
  {
    store: "TRMStatusMetadataList",
    listName: ListNames.TRMStatusMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy: [{ orderBy: "StatusCode", ascending: true }],
    indexBasedOn: "modifiedOn",
    priority: "critical"
  },
  {
    store: "TRMNotificationsMetadataList",
    listName: ListNames.TRMNotificationsMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy: [],
    indexBasedOn: "modifiedOn",
    priority: "deferred"
  },
  {
    store: "TRMActvityLogMetadataList",
    listName: ListNames.TRMActvityLogMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy: [],
    indexBasedOn: "modifiedOn",
    priority: "deferred"
  },
  {
    store: "TRMProcessFlowMetadataList",
    listName: ListNames.TRMProcessFlowMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy: [],
    indexBasedOn: "modifiedOn",
    priority: "deferred"
  }
];

const CRITICAL_METADATA_CONFIGS = METADATA_CONFIGS.filter(cfg => cfg.priority === "critical");

const DEFERRED_METADATA_CONFIGS = METADATA_CONFIGS.filter(cfg => cfg.priority === "deferred");

export const getAllMetadata = async (MetadataConfigs: MetadataConfig[]) => {
  const results: Record<string, any[]> = {};

  // Each config owns a separate IndexedDB database (TRM-DB_<store>), so these loads
  // share no transaction or store lock and are safe to run concurrently. The per-config
  // try/catch is kept inside the task so one failing list cannot take down the rest.
  const settled = await Promise.all(MetadataConfigs.map(async (cfg) => {
    const { store, listName, select, expand, filter, orderBy , indexBasedOn } = cfg;

    
    try {
      const lastModifiedDate = await getLatestModifiedDate(store, indexBasedOn);

      let combinedFilter = filter || "";
      if (lastModifiedDate) {
        const dateFilter = `${indexBasedOn} gt ${toODataDate(lastModifiedDate)}`;
        combinedFilter = combinedFilter
          ? `${combinedFilter} and ${dateFilter}`
          : dateFilter;
      }

      const list = await anewRListsHttp().getList(listName);
      const query = list.query()
      
      if (select.length > 0) query.select(...select);
      if (expand.length > 0) query.expand(...expand);
      if (combinedFilter !== "") query.filter(combinedFilter);
      if (orderBy.length > 0) query.orderBy(orderBy);

      const updatedItemsIterable = await query.get();
      const updatedItems = Array.isArray(updatedItemsIterable)
        ? updatedItemsIterable
        : Array.from(updatedItemsIterable || []);

      if (updatedItems.length > 0) {
        await saveItems(store, updatedItems);
      }

      return { listName, items: await getAllItemsInStore(store) };
    } catch (error) {
        console.error(`Error processing metadata for store ${store}:`, error);
        return null;
    }
  }));

  // Fold in declaration order. Two configs may share a listName while mirroring into
  // different IndexedDB stores (FORMMETADATA_CONFIGS does, for TRMSubClientTaxExemptionList),
  // and the sequential version always let the later config win. Resolving by completion
  // order instead would make the result depend on which request happened to finish first.
  for (const entry of settled) {
    if (entry) {
      results[entry.listName] = entry.items;
    }
  }

  return results;
};


const FORMMETADATA_CONFIGS = [
  {
    store: "TRMAccountSettingMetadataList",
    listName: ListNames.TRMAccountSettingMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
  {
    store: "TRMSubClientSettingMetadataList",
    listName: ListNames.TRMSubClientSettingMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
  {
    store: "TRMSubClientTaxExemptionMetadataList",
    listName: ListNames.TRMSubClientTaxExemptionList,
    select: [],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
  
  {
    store: "TRMTooltipMetadataList",
    listName: ListNames.TRMTooltipMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
  {
    store: "TRMCountyMetadataList",
    listName: ListNames.TRMCountyMetadataList,
    select: ["County", "State", "Territory", "eKey", "ItemModified","modifiedOn"],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
  {
    store: "TRMSubClientTaxExemptionList",
    listName: ListNames.TRMSubClientTaxExemptionList,
    select: [],
    expand: [],
    filter: "",
    orderBy: [],
    indexBasedOn: "modifiedOn"
  },
  {
    store: "TRMAccTaxExemptionList",
    listName: ListNames.TRMAccTaxExemptionList,
    select: [],
    expand: [],
    filter: "",
    orderBy: [],
    indexBasedOn: "modifiedOn"
  },
];

const PROCESSFLOWMETADATA_CONFIGS = [
  {
    store: "TRMProcessFlowMetadataList",
    listName: ListNames.TRMProcessFlowMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
];

const REPORTSMETADATA_CONFIGS = [
  {
    store: "TRMAccountSettingMetadataList",
    listName: ListNames.TRMAccountSettingMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
  {
    store: "TRMSubClientSettingMetadataList",
    listName: ListNames.TRMSubClientSettingMetadataList,
    select: [],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
  {
    store: "TRMCountyMetadataList",
    listName: ListNames.TRMCountyMetadataList,
    select: ["County", "State", "Territory", "eKey", "ItemModified","modifiedOn"],
    expand: [],
    filter: "",
    orderBy:[],
    indexBasedOn: "modifiedOn"
  },
];


export const getAllFormMetadata = async () => {
  return getAllMetadata(FORMMETADATA_CONFIGS);
}
export const getAllInitialMetadata = async () => {
  return getAllMetadata(CRITICAL_METADATA_CONFIGS);
}

/**
 * The in-flight (or last completed) deferred load. Deferred metadata drives write
 * paths - notification dispatch and activity-log text - so callers that fire before
 * hydration finishes must be able to await it rather than silently operating on [].
 */
let deferredMetadataPromise: Promise<Record<string, any[]>> | null = null;

const loadDeferredMetadata = (): Promise<Record<string, any[]>> => {
  const request = getAllMetadata(DEFERRED_METADATA_CONFIGS);
  deferredMetadataPromise = request;
  request.catch(() => {
    // Never cache a rejection - the next caller should be able to retry.
    if (deferredMetadataPromise === request) {
      deferredMetadataPromise = null;
    }
  });
  return request;
}

/** Starts a fresh deferred load. Used by MetadataContextProvider on (re)hydration. */
export const getAllDeferredMetadata = () => loadDeferredMetadata();

/** Resolves with deferred metadata, starting a load only if none has run yet. */
export const ensureDeferredMetadata = () => deferredMetadataPromise ?? loadDeferredMetadata();

/**
 * Returns `current` when it is already populated, otherwise waits for the deferred
 * hydration to finish and reads the list from it. Lets a user action that lands
 * during the hydration window block briefly instead of no-op'ing on an empty array.
 */
export const ensureDeferredList = async <T = any>(listName: string, current?: T[] | null): Promise<T[]> => {
  if (current?.length) {
    return current;
  }
  try {
    const pending = ensureDeferredMetadata();
    const deferred = await pending;
    if (!(listName in deferred)) {
      // getAllMetadata swallows per-list errors, so a missing key means that list failed
      // rather than that it is genuinely empty. Drop the cached load so the next caller
      // retries instead of being served [] for the rest of the session.
      if (deferredMetadataPromise === pending) {
        deferredMetadataPromise = null;
      }
      return current || [];
    }
    return (deferred[listName] as T[]) || current || [];
  } catch (error) {
    console.error(`Deferred metadata unavailable for list "${listName}".`, error);
    return current || [];
  }
}

export const getAllProcessFlowMetadata = async () => {
  return getAllMetadata(PROCESSFLOWMETADATA_CONFIGS)
}

export const getAllReportsMetadata = async () => {
  return getAllMetadata(REPORTSMETADATA_CONFIGS)
}


